<template>
  <div id="sl-meal-to-list-picker">
    <h3 class="meal-name">{{ meal.name }}</h3>
    <h4 class="meal-view">Add ingredients to list</h4>
    <table class="table">
      <colgroup>
        <col>
        <col style="width: 7rem;">
        <col style="width: 7rem;">
        <col style="width: 6rem;">
      </colgroup>
      <thead>
        <tr>
          <th>Ingredient</th>
          <th>Stores</th>
          <th class="text-center">Needed</th>
          <th class="text-center">In List</th>
          <th class="text-center">To Add</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="ingr in meal.ingredients" :key="ingr.id" :class="{'not-needed': !addQuantityMap[ingr.id]}">
          <td class="name-notes">
            <div class="name">{{ ingr.name }}</div>
            <div class="notes">{{ ingr.notes }}</div>
          </td>
          <td class="store-list">
            <span v-for="s in ingr.storeList" :key="s.id" class="label label-rounded sl-store-label">{{ s.name }}</span>
          </td>
          <td class="qty-needed text-center">{{ ingr.quantityNeeded }}</td>
          <td class="qty-in-list text-center">{{ ingr.item ? ingr.item.quantityNeeded : 0 }}</td>
          <td class="qty-add text-center">
            <div v-if="busy">
              <span v-html="addQuantityMap[ingr.id]"></span>
            </div>
            <div class="form-group" v-else>
              <div class="input-group">
                <button @click.stop="quantityDown(ingr.id)"
                        class="btn btn-primary input-group-btn hide-xs"
                        :class="{'disabled': !addQuantityMap[ingr.id]}">
                  <fa-icon icon="angle-down"></fa-icon>
                </button>
                <input type="number" step="1" min="0" v-model="addQuantityMap[ingr.id]" class="form-input text-center">
                <button @click.stop="quantityUp(ingr.id)"
                        class="btn btn-primary input-group-btn hide-xs">
                  <fa-icon icon="angle-up"></fa-icon>
                </button>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td colspan="5">
            <div class="table-controls">
              <div v-if="busy" class="loading loading-xl"></div>
              <template v-if="!busy">
                <button class="btn btn-primary" @click.stop="processMeal"><span>Reset</span><fa-icon icon="sync"></fa-icon></button>
                <button class="btn btn-success" :class="{'disabled': !somethingToAdd}"
                        @click.stop="addToNeeded">
                  <fa-icon icon="clipboard-list"></fa-icon>
                  <span>Add {{this.ingredientsToBeAdded}} to list</span>
                </button>
              </template>
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
/*
  TODO:
    - Small viewport should use cards...?
*/

import _ from 'lodash'

export default {
  name: 'MealToNeededPicker',
  props: {
    meal: { type: Object, required: true }
  },
  data () {
    return {
      busy: false,
      addQuantityMap: {}
    }
  },
  computed: {
    somethingToAdd () {
      return _.reduce(_.values(this.addQuantityMap), (sum, qty) => sum + qty, 0)
    },
    ingredientsToBeAdded () {
      let ingrCount = _.reduce(_.values(this.addQuantityMap), (ct, qty) => ct + (qty > 0 ? 1 : 0), 0)
      return ingrCount === 1 ? '1 ingredient' : `${ingrCount} ingredients`
    }
  },
  mounted () {
    this.processMeal()
  },
  methods: {
    processMeal () {
      this.addQuantityMap = {}
      _.forEach(this.meal.ingredients, ingr => {
        if (!ingr.item) {
          const item = this.getItemForId(ingr.itemId)
          if (item) {
            ingr.name = item.name
            ingr.notes = item.notes
            ingr.item = item
            ingr.storeList = _.map(item.storeIds, sId => this.getStoreForId(sId))
          }
        }
        const qtyInList = ingr.item ? ingr.item.quantityNeeded : 0
        const qtyDiff = ingr.quantityNeeded - qtyInList
        this.$set(this.addQuantityMap, ingr.id, qtyDiff < 0 ? 0 : qtyDiff)
      })
    },
    quantityDown (ingrId) {
      if (this.addQuantityMap[ingrId] > 0) {
        this.$set(this.addQuantityMap, ingrId, this.addQuantityMap[ingrId] - 1)
      }
    },
    quantityUp (ingrId) {
      this.$set(this.addQuantityMap, ingrId, this.addQuantityMap[ingrId] + 1)
    },
    addToNeeded () {
      this.busy = true
      Promise.all(_.map(this.meal.ingredients, ingr => {
        const quantity = this.addQuantityMap[ingr.id]
        if (quantity && ingr.item) {
          const totalQuantity = quantity + ingr.item.quantityNeeded
          return this.setItemQuantityNeeded({ id: ingr.item.id, quantityNeeded: totalQuantity })
        }
      })).then(() => {
        this.busy = false
        this.$emit('done')
      })
    }
  },
  watch: {
    meal (newMeal) {
      newMeal && newMeal.id && this.processMeal()
    }
  }
}
</script>

<style lang="scss">
  @import '../assets/_vars';

  #sl-meal-to-list-picker {
    .meal-name {
      text-align: center;
      color: $light-color;
    }
    .meal-view {
      text-align: center;
      font-size: 1rem;
      color: darken($light-color, 20%);
    }

    .table {
      tbody {
        tr {
          td:last-of-type {
            width: 6rem;
          }
          td {
            &.name-notes {
              .name {
                font-size: 1rem;
              }
              .notes {
                font-size: 0.75rem;
                font-style: italic;
                color: lighten($dark-color, 30%);
              }
            }
            &.qty-add {
              color: black;
            }
          }
          &.not-needed {
            td {
              &.name-notes {
                color: $darkest-color;
                .notes {
                  color: $darkest-color;
                }
              }
              &.qty-needed, &.qty-in-list {
                color: $darkest-color;
              }
              &.store-list {
                opacity: 0.2;
              }
            }
          }
        }
      }
    }
  }
</style>
